import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { usePetPolicies } from '../../../hooks/usePetPolicies';
import Shimmer from '../../common/Shimmer';
import { PowerupInfoCard } from './PowerupInfoCard';
import { getOrcaPowerUpsBatch } from '../../../actions';

import './css/PowerupInfoCard.css';
import { MARKETING_CHANNEL_ID } from '../../../theme/theme.constants';
import {
  POWER_UPS_RELATION,
} from '../../changeCoverage/powerups/powerups.constans';
import { PRODUCT_FAMILY_IDS } from '../../../constants';

const Powerups = ({
  company = MARKETING_CHANNEL_ID.figo,
  items = [],
  productFamilyId = PRODUCT_FAMILY_IDS.fgo,
  onClickLearnMore = () => { },
}) => {
  const { t } = useTranslation('insurance');
  const dispatch = useDispatch();
  const petPolicies = usePetPolicies();
  const { loading } = petPolicies;

  const { powerUpDetailsByKey } = useSelector(({ orcaInsurance }) => ({
    powerUpDetailsByKey: orcaInsurance.powerUpDetailsByKey,
  }), shallowEqual);

  const preventiveItems = useMemo(() => items
    .filter(({ ContentfulKey }) => ContentfulKey?.includes('Preventive')),
  [items]);

  const hasPreventiveItems = preventiveItems.length > 0;

  useEffect(() => {
    const productKeys = preventiveItems
      .map(({ ContentfulKey }) => ContentfulKey)
      .filter(Boolean);

    if (productKeys.length) {
      dispatch(getOrcaPowerUpsBatch({ productKeys }));
    }
  }, [preventiveItems, dispatch]);

  const isOptionalPreventive = hasPreventiveItems
    || productFamilyId === PRODUCT_FAMILY_IDS.ipg;

  const title = useMemo(() => {
    if (isOptionalPreventive) {
      return t('optionalPreventiveTitle');
    }

    if (company !== MARKETING_CHANNEL_ID.figo) {
      return t('upgradesTitle');
    }

    return t('powerUpsTitle');
  }, [company, isOptionalPreventive, t]);

  const renderPowerUpInfo = useCallback(() => (
    items.map((benefits, index) => {
      let name = benefits.Name;
      let wellnessTypeId = null;

      if (benefits.Included && benefits
        .InsuranceModifierId === POWER_UPS_RELATION.WELLNESS_PSM_ID) {
        const includedWellness = benefits.OptionalBenefits?.find(({
          IsIncluded,
        }) => IsIncluded);

        name = includedWellness?.CategoryHeader;
        wellnessTypeId = includedWellness?.Id;
      }

      return (
        <PowerupInfoCard
          key={benefits.Id || String(index)}
          contentfulKey={benefits?.ContentfulKey}
          description={benefits.Description}
          included={benefits.Included}
          name={name}
          onClickLearnMore={onClickLearnMore}
          optionalBenefits={benefits.OptionalBenefits}
          wellnessTypeId={wellnessTypeId}
        />
      );
    })
  ), [items]);

  const renderIPGPowerUpInfo = useCallback(() => {
    if (hasPreventiveItems) {
      return preventiveItems.map((benefits, index) => (
        <PowerupInfoCard
          key={benefits.Id || String(index)}
          contentfulKey={benefits?.ContentfulKey}
          description={powerUpDetailsByKey[benefits.ContentfulKey]
            ?.data?.DescriptionHTML}
          included={benefits.Included}
          name={benefits.Name}
          onClickLearnMore={onClickLearnMore}
        />
      ));
    }

    // IPG only should show wellness powerups benefits
    const onlyWellness = items
      .find(({ InsuranceModifierId }) => InsuranceModifierId
        === POWER_UPS_RELATION.WELLNESS_PSM_ID);

    return (
      onlyWellness?.OptionalBenefits?.map((benefits) => {
        const details = benefits.OptionalBenefitsDetailsItems[0];

        return (
          <PowerupInfoCard
            key={String(benefits.Id)}
            description={details?.ItemSubtext}
            included={benefits?.IsIncluded}
            name={details?.ItemLabel}
            wellnessTypeId={benefits.Id}
          />
        );
      }) || []
    );
  }, [
    hasPreventiveItems,
    items,
    onClickLearnMore,
    powerUpDetailsByKey,
    preventiveItems,
  ]);

  if (loading) {
    return (
      <Shimmer height={30} />
    );
  }

  return (
    <div className="Powerup-info-section-container">
      <h2>
        {title}
      </h2>

      <div className="Powerup-info-grid-container">
        {isOptionalPreventive
          ? renderIPGPowerUpInfo()
          : renderPowerUpInfo()}
      </div>
    </div>
  );
};

export { Powerups };
